import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { newsList } from "@/mocks/news";
import { usePageMeta } from "@/hooks/usePageMeta";
import Breadcrumb from "@/components/base/Breadcrumb";
import Pagination from "@/components/base/Pagination";
import type { NewsCategory, NewsItem } from "@/types";

const PAGE_SIZE = 4;

const categoryTabs: { value: "全部" | NewsCategory; label: string }[] = [
  { value: "全部", label: "全部" },
  { value: "新闻", label: "新闻" },
  { value: "剧情解读", label: "剧情解读" },
  { value: "角色访谈", label: "角色访谈" },
];

export default function News() {
  const [activeCategory, setActiveCategory] = useState<"全部" | NewsCategory>("全部");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<NewsItem | null>(null);

  usePageMeta(
    "仙剑奇侠传资讯专题 - 剧情解读与角色访谈",
    "仙剑奇侠传及仙侠影视最新资讯专题，涵盖官方动态、剧情深度解读与角色访谈，第一时间了解仙侠影视热门话题。",
    "仙剑奇侠传资讯,仙侠剧新闻,仙剑剧情解读,角色访谈"
  );

  const filtered = useMemo(() => {
    const list =
      activeCategory === "全部"
        ? newsList
        : newsList.filter((item) => item.category === activeCategory);
    return [...list].sort((a, b) => (a.date < b.date ? 1 : -1));
  }, [activeCategory]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pagedList = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const hotNews = useMemo(() => [...newsList].sort((a, b) => (a.date < b.date ? 1 : -1)).slice(0, 4), []);

  useEffect(() => {
    if (!selected) return;
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setSelected(null);
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [selected]);

  const handleCategoryChange = (value: "全部" | NewsCategory) => {
    setActiveCategory(value);
    setPage(1);
  };

  return (
    <div className="w-full">
      <div className="w-full px-4 md:px-8 pt-6 md:pt-8">
        <Breadcrumb items={[{ label: "资讯专题" }]} />
      </div>

      <section className="w-full px-4 md:px-8 py-6 md:py-8">
        <h1 className="font-heading text-2xl md:text-4xl font-semibold text-foreground-950">
          资讯专题
        </h1>
        <p className="mt-3 text-sm md:text-base text-foreground-600 max-w-3xl leading-relaxed">
          汇集仙剑奇侠传及仙侠影视的最新资讯、剧情深度解读与角色访谈，帮助剧迷全面了解作品背后的故事与话题。
        </p>

        <div className="mt-6 inline-flex flex-wrap items-center gap-1 p-1 rounded-full bg-background-100 border border-background-200">
          {categoryTabs.map((tab) => (
            <button
              key={tab.value}
              type="button"
              onClick={() => handleCategoryChange(tab.value)}
              className={`px-4 py-2 rounded-full text-sm whitespace-nowrap transition-colors cursor-pointer ${
                tab.value === activeCategory
                  ? "bg-primary-700 text-background-50"
                  : "text-foreground-700 hover:bg-secondary-100 hover:text-secondary-900"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </section>

      <div className="w-full px-4 md:px-8 pb-16 grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-8">
          {pagedList.length > 0 ? (
            <>
              <ul className="flex flex-col gap-4">
                {pagedList.map((item) => (
                  <li key={item.id}>
                    <button
                      type="button"
                      onClick={() => setSelected(item)}
                      className="group w-full text-left flex flex-col sm:flex-row gap-4 p-3 md:p-4 rounded-lg border border-background-200 bg-background-50 hover:border-primary-300 transition-colors cursor-pointer"
                    >
                      <span className="w-full sm:w-48 h-44 sm:h-32 rounded-md overflow-hidden shrink-0 bg-background-200">
                        <img
                          src={item.cover}
                          alt={item.title}
                          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                        />
                      </span>
                      <span className="min-w-0 flex flex-col">
                        <span className="flex items-center gap-2">
                          <span className="px-2 py-0.5 rounded-full bg-accent-100 text-accent-800 text-[11px]">
                            {item.category}
                          </span>
                          <span className="text-xs text-foreground-400">{item.date}</span>
                        </span>
                        <span className="mt-2 text-base md:text-lg font-medium font-heading text-foreground-950 leading-snug line-clamp-2 group-hover:text-primary-700 transition-colors">
                          {item.title}
                        </span>
                        <span className="mt-2 text-sm text-foreground-500 leading-relaxed line-clamp-2">
                          {item.summary}
                        </span>
                        <span className="mt-3 inline-flex items-center gap-1 text-xs text-primary-700">
                          阅读全文
                          <i className="ri-arrow-right-line"></i>
                        </span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
              <div className="mt-10">
                <Pagination page={currentPage} totalPages={totalPages} onChange={setPage} />
              </div>
            </>
          ) : (
            <div className="rounded-lg border border-dashed border-background-300 bg-background-50 py-20 px-6 text-center">
              <span className="mx-auto w-16 h-16 rounded-full bg-secondary-100 text-secondary-700 flex items-center justify-center text-3xl">
                <i className="ri-newspaper-line"></i>
              </span>
              <h3 className="mt-5 font-heading text-lg text-foreground-950">
                该分类下暂无资讯
              </h3>
              <p className="mt-2 text-sm text-foreground-500">
                换个分类看看，或者浏览下方热门资讯内容。
              </p>
              <button
                type="button"
                onClick={() => handleCategoryChange("全部")}
                className="mt-6 inline-flex items-center gap-2 h-10 px-6 rounded-md bg-primary-700 hover:bg-primary-600 text-background-50 text-sm font-medium whitespace-nowrap transition-colors cursor-pointer"
              >
                查看全部资讯
              </button>
            </div>
          )}
        </div>

        <aside className="lg:col-span-4">
          <div className="rounded-lg border border-background-200 bg-background-50 p-5 lg:sticky lg:top-24">
            <h2 className="font-heading text-lg font-semibold text-foreground-950 mb-4 flex items-center gap-2">
              <i className="ri-fire-line text-accent-600"></i>
              热门资讯
            </h2>
            <ul className="flex flex-col gap-4">
              {hotNews.map((item, index) => (
                <li key={item.id}>
                  <button
                    type="button"
                    onClick={() => setSelected(item)}
                    className="group w-full text-left flex items-start gap-3 cursor-pointer"
                  >
                    <span
                      className={`w-6 h-6 rounded-md flex items-center justify-center text-xs shrink-0 mt-0.5 ${
                        index < 3
                          ? "bg-accent-500 text-foreground-950"
                          : "bg-background-100 text-foreground-500"
                      }`}
                    >
                      {index + 1}
                    </span>
                    <span className="min-w-0">
                      <span className="block text-sm text-foreground-800 leading-snug line-clamp-2 group-hover:text-primary-700 transition-colors">
                        {item.title}
                      </span>
                      <span className="block text-xs text-foreground-400 mt-1">
                        {item.date}
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
            <Link
              to="/category"
              className="mt-6 inline-flex items-center gap-2 text-sm text-primary-700 hover:text-primary-800 whitespace-nowrap"
            >
              浏览全部剧集
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </aside>
      </div>

      {selected && (
        <div
          className="fixed inset-0 z-[70] flex items-end md:items-center justify-center p-0 md:p-6 bg-foreground-950/60 animate-fade-in"
          onClick={() => setSelected(null)}
          role="presentation"
        >
          <div
            className="w-full md:max-w-2xl max-h-[88vh] overflow-y-auto rounded-t-lg md:rounded-lg bg-background-50"
            onClick={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label={selected.title}
          >
            <div className="relative h-52 md:h-64">
              <img
                src={selected.cover}
                alt={selected.title}
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/40 to-transparent"></div>
              <button
                type="button"
                onClick={() => setSelected(null)}
                aria-label="关闭"
                className="absolute top-4 right-4 w-9 h-9 rounded-full bg-black/40 hover:bg-black/60 text-white flex items-center justify-center transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <div className="px-5 md:px-8 pb-8 -mt-8 relative">
              <div className="flex items-center gap-2">
                <span className="px-2.5 py-1 rounded-full bg-accent-100 text-accent-800 text-xs">
                  {selected.category}
                </span>
                <span className="text-xs text-foreground-400">{selected.date}</span>
              </div>
              <h2 className="mt-3 font-heading text-xl md:text-2xl font-semibold text-foreground-950 leading-snug">
                {selected.title}
              </h2>
              <div className="mt-5 space-y-4">
                {selected.content.map((paragraph, index) => (
                  <p key={index} className="text-sm md:text-base text-foreground-700 leading-[1.9]">
                    {paragraph}
                  </p>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}